import { useEffect, useState } from "react";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { QRStudio } from "@/components/QRStudio";
import { Loader2, Download, Copy, Check, ExternalLink, QrCode, Sparkles } from "lucide-react";
import { toast } from "sonner";

export function QRPanel({ property }) {
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [studio, setStudio] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/properties/${property.id}/qr`, { params: { base_url: window.location.origin } })
      .then(({ data }) => setQr(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [property.id]);

  const cover = mediaUrl(property.branding?.cover) || "https://images.unsplash.com/photo-1720247520862-7e4b14176fa8?crop=entropy&cs=srgb&fm=jpg&q=85&w=600";

  const download = () => {
    const a = document.createElement("a");
    a.href = qr.qr;
    a.download = `${property.slug}-qr.png`;
    a.click();
  };
  const copyLink = () => { navigator.clipboard.writeText(qr.url); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 1500); };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;
  if (!qr) return null;

  return (
    <div className="grid sm:grid-cols-2 gap-6 items-center" data-testid={`qr-panel-${property.id}`}>
      <div className="flex flex-col items-center">
        <div className="rounded-3xl bg-white border border-slate-100 p-6 shadow-sm">
          <img src={qr.qr} alt="QR code" className="w-52 h-52" data-testid="qr-image" />
        </div>
        <p className="mt-3 text-sm text-slate-500">Scan to view your guest guide</p>
        <p className="font-display font-bold text-slate-900">{property.name}</p>
      </div>
      <div>
        <div className="relative rounded-2xl overflow-hidden h-40">
          <img src={cover} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-900/30 flex items-end p-4">
            <p className="text-white font-serif-display text-xl">{property.name}</p>
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-2">
          <button onClick={() => setStudio(true)} data-testid="open-qr-studio" className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold rounded-xl py-3 hover:bg-blue-700"><Sparkles className="w-4 h-4" /> Personalizza e condividi</button>
          <button onClick={download} data-testid="qr-download" className="inline-flex items-center justify-center gap-2 bg-slate-900 text-white font-semibold rounded-xl py-3 hover:bg-slate-800"><Download className="w-4 h-4" /> Scarica QR</button>
          <button onClick={copyLink} data-testid="qr-copy" className="inline-flex items-center justify-center gap-2 border border-slate-200 text-slate-700 font-semibold rounded-xl py-3 hover:bg-slate-50">{copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />} Copia link</button>
          <a href={qr.url} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 border border-slate-200 text-slate-700 font-semibold rounded-xl py-3 hover:bg-slate-50"><ExternalLink className="w-4 h-4" /> Apri guida</a>
        </div>
      </div>
      <QRStudio property={property} open={studio} onClose={() => setStudio(false)} />
    </div>
  );
}

export default function QRCodes() {
  const [props, setProps] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/properties").then(({ data }) => {
      const pub = data.properties.filter((p) => p.status === "published");
      setProps(pub);
      setActive(pub[0] || null);
    });
  }, []);

  if (!props) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;

  return (
    <div>
      <h1 className="font-display font-extrabold text-3xl tracking-tight text-slate-900">QR Codes</h1>
      <p className="mt-1 text-slate-500">Print a QR code so guests can open the guide instantly.</p>
      {props.length === 0 ? (
        <div className="mt-8 rounded-3xl border-2 border-dashed border-slate-200 p-12 text-center">
          <QrCode className="w-10 h-10 mx-auto text-slate-300" />
          <p className="mt-4 font-display font-bold text-lg text-slate-800">No published guides yet</p>
          <p className="text-slate-500 mt-1">Publish a guide to generate its QR code.</p>
        </div>
      ) : (
        <>
          <div className="mt-6 flex gap-2 flex-wrap">
            {props.map((p) => (
              <button key={p.id} onClick={() => setActive(p)} className={`text-sm font-semibold px-4 py-2 rounded-full border transition-colors ${active?.id === p.id ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                {p.name}
              </button>
            ))}
          </div>
          {active && <div className="mt-8 rounded-3xl border border-slate-100 bg-white p-8"><QRPanel property={active} /></div>}
        </>
      )}
    </div>
  );
}
